import { Chip } from "../../components/Chip";
import { cn } from "../../lib/utils";
import React, { useState } from "react";
// Constants for consistent styling
const VALUE_CLASSES = "s-text-primary-700 dark:s-text-primary-700-night s-pt-1 s-text-sm";
const EMPTY_CLASSES = "s-text-primary-500 dark:s-text-primary-500-night s-pt-1 s-text-sm s-italic";
const INDENT_CLASSES = "s-border-structure-200 dark:s-border-structure-200-night s-max-w-full s-border-l s-pl-4 s-ml-4";
// Performance limits to prevent browser crashes.
// These limits are meant to be very conservative.
const MAX_OBJECT_DEPTH = 8;
const MAX_ARRAY_ITEMS = 128;
const MAX_OBJECT_KEYS = 64;
const MAX_STRING_LENGTH = 1024;
// Helper component for inline expand/collapse buttons with consistent styling.
function InlineExpandButton({ label, buttonText, onClick, className, }) {
    return (React.createElement("span", { className: cn(EMPTY_CLASSES, className) },
        label,
        " ",
        React.createElement("button", { onClick: onClick, className: "s-cursor-pointer s-font-medium s-text-highlight hover:s-underline dark:s-text-highlight-night" }, buttonText)));
}
// Helper component for rendering key-value pairs with consistent styling.
function KeyValuePair({ keyName, value, depth, chipColor, isRootLevel = false, expandedPaths, setExpandedPaths, currentPath, }) {
    const isComplexValue = typeof value === "object" && value !== null;
    if (isComplexValue) {
        return (React.createElement(React.Fragment, null,
            React.createElement(Chip, { size: "xs", color: chipColor, label: formatKey(keyName), className: "s-mb-2" }),
            React.createElement("div", { className: cn("s-max-w-full", isRootLevel && "s-ml-4") },
                React.createElement(JsonValue, { value: value, depth: depth + 1, expandedPaths: expandedPaths, setExpandedPaths: setExpandedPaths, currentPath: `${currentPath}.${keyName}` }))));
    }
    return (React.createElement("div", { className: cn("s-flex s-items-start", isRootLevel ? "s-gap-3" : "s-gap-2") },
        React.createElement(Chip, { size: "xs", color: chipColor, label: formatKey(keyName) }),
        React.createElement(JsonValue, { value: value, depth: depth + 1, expandedPaths: expandedPaths, setExpandedPaths: setExpandedPaths, currentPath: `${currentPath}.${keyName}` })));
}
function JsonValue({ value, depth = 0, expandedPaths = new Set(), setExpandedPaths, currentPath = "", }) {
    var _a, _b, _c, _d;
    const handleToggleExpanded = (path) => {
        if (!setExpandedPaths) {
            return;
        }
        setExpandedPaths((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(path)) {
                newSet.delete(path);
            }
            else {
                newSet.add(path);
            }
            return newSet;
        });
    };
    if (depth >= MAX_OBJECT_DEPTH &&
        typeof value === "object" &&
        value !== null) {
        const deepObjectPath = `${currentPath}:deep`;
        const isExpanded = (_a = expandedPaths === null || expandedPaths === void 0 ? void 0 : expandedPaths.has(deepObjectPath)) !== null && _a !== void 0 ? _a : false;
        if (isExpanded) {
            // Render the full object/array when expanded, ignoring depth limit.
            return (React.createElement(JsonValue, { value: value, depth: 0, expandedPaths: expandedPaths, setExpandedPaths: setExpandedPaths, currentPath: `${currentPath}:expanded` }));
        }
        return (React.createElement("div", { className: "s-flex s-items-center s-gap-1" },
            React.createElement(InlineExpandButton, { label: "Maximum depth reached", buttonText: "expand", onClick: () => handleToggleExpanded(deepObjectPath) })));
    }
    if (value === null || value === undefined) {
        return React.createElement("span", { className: EMPTY_CLASSES }, "empty");
    }
    if (typeof value === "boolean") {
        return React.createElement("span", { className: VALUE_CLASSES }, value ? "Yes" : "No");
    }
    if (typeof value === "number") {
        return React.createElement("span", { className: VALUE_CLASSES }, value);
    }
    if (typeof value === "string") {
        if (value.length > MAX_STRING_LENGTH) {
            const longStringPath = `${currentPath}:longstring`;
            const isExpanded = (_b = expandedPaths === null || expandedPaths === void 0 ? void 0 : expandedPaths.has(longStringPath)) !== null && _b !== void 0 ? _b : false;
            return (React.createElement("span", { className: cn(VALUE_CLASSES, "s-whitespace-pre-wrap s-break-normal") },
                isExpanded ? value : value.substring(0, MAX_STRING_LENGTH),
                !isExpanded && "…",
                " ",
                React.createElement("button", { onClick: () => handleToggleExpanded(longStringPath), className: "s-cursor-pointer s-font-medium s-text-highlight hover:s-underline dark:s-text-highlight-night" }, isExpanded
                    ? "collapse"
                    : `expand (${(value.length - MAX_STRING_LENGTH).toLocaleString()} more characters)`)));
        }
        return (React.createElement("span", { className: cn(VALUE_CLASSES, "s-whitespace-pre-wrap s-break-normal") }, value));
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return React.createElement("span", { className: EMPTY_CLASSES }, "empty list");
        }
        // Check if it's a simple array of primitives.
        const isSimpleArray = value.every((item) => typeof item !== "object" || item === null);
        if (isSimpleArray && value.length <= 5) {
            return (React.createElement("span", { className: VALUE_CLASSES }, value.map((item, index) => (React.createElement("span", { key: index },
                React.createElement(JsonValue, { value: item, depth: depth + 1, expandedPaths: expandedPaths, setExpandedPaths: setExpandedPaths, currentPath: `${currentPath}[${index}]` }),
                index < value.length - 1 && ", ")))));
        }
        // Truncate arrays that have too many items.
        const arrayPath = `${currentPath}[]`;
        const isExpanded = (_c = expandedPaths === null || expandedPaths === void 0 ? void 0 : expandedPaths.has(arrayPath)) !== null && _c !== void 0 ? _c : false;
        const itemsToShow = isExpanded
            ? value.length
            : Math.min(value.length, MAX_ARRAY_ITEMS);
        const hasMore = value.length > MAX_ARRAY_ITEMS && !isExpanded;
        return (React.createElement("div", { className: "s-mt-2" },
            value.slice(0, itemsToShow).map((item, index) => (React.createElement("div", { key: index, className: cn(INDENT_CLASSES) },
                React.createElement("div", { className: "s-flex s-flex-col s-gap-2" },
                    React.createElement(Chip, { size: "xs", color: "primary", label: `Item ${index + 1}` }),
                    React.createElement("div", { className: "s-max-w-full" },
                        React.createElement(JsonValue, { value: item, depth: depth + 1, expandedPaths: expandedPaths, setExpandedPaths: setExpandedPaths, currentPath: `${currentPath}[${index}]` })))))),
            hasMore && (React.createElement("div", { className: cn(INDENT_CLASSES) },
                React.createElement(InlineExpandButton, { label: `${value.length - itemsToShow} more items`, buttonText: "expand", onClick: () => handleToggleExpanded(arrayPath) })))));
    }
    if (typeof value === "object") {
        const entries = Object.entries(value);
        if (entries.length === 0) {
            return React.createElement("span", { className: EMPTY_CLASSES }, "empty");
        }
        // Truncate objects with too many properties.
        const objectPath = `${currentPath}{}`;
        const isExpanded = (_d = expandedPaths === null || expandedPaths === void 0 ? void 0 : expandedPaths.has(objectPath)) !== null && _d !== void 0 ? _d : false;
        const keysToShow = isExpanded
            ? entries.length
            : Math.min(entries.length, MAX_OBJECT_KEYS);
        const hasMore = entries.length > MAX_OBJECT_KEYS && !isExpanded;
        const visibleEntries = entries.slice(0, keysToShow);
        // For nested objects, use a card-like layout with vertical bars.
        if (depth > 0) {
            return (React.createElement("div", { className: "s-space-y-2" },
                visibleEntries.map(([key, val]) => (React.createElement("div", { key: key, className: cn(INDENT_CLASSES) },
                    React.createElement(KeyValuePair, { keyName: key, value: val, depth: depth, chipColor: "info", expandedPaths: expandedPaths, setExpandedPaths: setExpandedPaths, currentPath: currentPath })))),
                hasMore && (React.createElement("div", { className: cn(INDENT_CLASSES) },
                    React.createElement(InlineExpandButton, { label: `${entries.length - keysToShow} more properties`, buttonText: "expand", onClick: () => handleToggleExpanded(objectPath) })))));
        }
        // Root level objects use a table-like layout.
        return (React.createElement("div", { className: "s-max-w-full s-space-y-3" },
            visibleEntries.map(([key, val]) => (React.createElement("div", { key: key, className: cn("s-max-w-full s-border-b s-pb-3 last:s-border-0 last:s-pb-0", "s-border-structure-200 dark:s-border-structure-200-night") },
                React.createElement(KeyValuePair, { keyName: key, value: val, depth: depth, chipColor: "highlight", isRootLevel: true, expandedPaths: expandedPaths, setExpandedPaths: setExpandedPaths, currentPath: currentPath })))),
            hasMore && (React.createElement("div", { className: "s-border-structure-200 dark:s-border-structure-200-night s-border-t s-pt-3" },
                React.createElement(InlineExpandButton, { label: `${entries.length - keysToShow} more properties`, buttonText: "expand", onClick: () => handleToggleExpanded(objectPath) })))));
    }
    return (React.createElement("span", { className: cn("s-text-sm", "s-text-element-700 dark:s-text-element-700-night") }, String(value)));
}
function formatKey(key) {
    // Convert snake_case or camelCase to Title Case.
    return key
        .replace(/_/g, " ")
        .replace(/([A-Z])/g, " $1")
        .trim()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
}
export function PrettyJsonViewer({ data, className }) {
    const [expandedPaths, setExpandedPaths] = useState(new Set());
    return (React.createElement("div", { className: cn("s-max-w-full s-min-w-0 s-overflow-x-auto s-overflow-y-visible", "s-bg-structure-50 dark:s-bg-structure-50-night", "s-rounded-lg s-px-4 s-py-4 s-text-base", className) },
        React.createElement("div", { className: "s-max-w-full s-overflow-x-auto" },
            React.createElement(JsonValue, { value: data, expandedPaths: expandedPaths, setExpandedPaths: setExpandedPaths, currentPath: "root" }))));
}
//# sourceMappingURL=PrettyJsonViewer.js.map